/* REFACTOR-SPLIT (QOE-RECON-1, FILE_SIZE_POLICY): 741 -> 202 lines. Not
 * truncation. The removed content moved verbatim to frontend/src/pages/
 * entityStructure/{AddEntityForm,EntityTable,PreliminaryStampBanner,
 * ConsolidationResults,ReconciliationPanel}.tsx and shared.ts, all staged
 * in this same commit. */
import { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { Network, Plus, Play, AlertTriangle } from 'lucide-react'
import {
  listDealEntities,
  createDealEntity,
  deleteDealEntity,
  runConsolidation,
  type DealEntity,
  type ConsolidationResult,
  type PreliminaryStamp,
} from '@/api/client'
import { useAuth } from '@/contexts/AuthContext'
import { formatApiError } from '@/utils/httpErrors'
import AddEntityForm, {
  type AddEntityFormState,
  DEFAULT_ENTITY_FORM,
} from './entityStructure/AddEntityForm'
import EntityTable from './entityStructure/EntityTable'
import PreliminaryStampBanner from './entityStructure/PreliminaryStampBanner'
import ConsolidationResults from './entityStructure/ConsolidationResults'

export default function EntityStructurePage() {
  const { dealId } = useParams<{ dealId: string }>()
  const { user } = useAuth()

  const [entities, setEntities] = useState<DealEntity[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddForm, setShowAddForm] = useState(false)
  const [form, setForm] = useState<AddEntityFormState>(DEFAULT_ENTITY_FORM)
  const [formError, setFormError] = useState('')
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [consolidating, setConsolidating] = useState(false)
  const [result, setResult] = useState<ConsolidationResult | null>(null)
  const [stamp, setStamp] = useState<PreliminaryStamp | undefined>(undefined)
  const [error, setError] = useState('')

  const fetchEntities = useCallback(async () => {
    if (!dealId) return
    try {
      const data = await listDealEntities(dealId)
      setEntities(data.entities)
      setStamp(data.preliminary_stamp)
    } catch (err) {
      setError(formatApiError(err))
    } finally {
      setLoading(false)
    }
  }, [dealId])

  useEffect(() => {
    fetchEntities()
  }, [fetchEntities])

  const handleAdd = async () => {
    if (!form.name.trim()) {
      setFormError('Entity name is required')
      return
    }
    if (form.ownership_pct < 0 || form.ownership_pct > 100) {
      setFormError('Ownership % must be between 0 and 100')
      return
    }
    setSaving(true)
    setFormError('')
    try {
      await createDealEntity(dealId!, {
        name: form.name.trim(),
        entity_type: form.entity_type,
        ownership_pct: form.ownership_pct,
        consolidation_method: form.consolidation_method,
        is_primary: form.is_primary,
        notes: form.notes.trim() || undefined,
        carved_out: form.carved_out,
        guarantees_debt: form.guarantees_debt,
        exists_to_serve_group: form.exists_to_serve_group,
        parent_entity_id: form.parent_entity_id || null,
      })
      setForm(DEFAULT_ENTITY_FORM)
      setShowAddForm(false)
      await fetchEntities()
    } catch (err) {
      setFormError(formatApiError(err))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (entityId: string) => {
    if (!window.confirm('Remove this entity from the deal?')) return
    setDeleting(entityId)
    try {
      await deleteDealEntity(dealId!, entityId)
      await fetchEntities()
    } catch (err) {
      setError(formatApiError(err))
    } finally {
      setDeleting(null)
    }
  }

  const handleConsolidate = async () => {
    if (entities.length < 2) {
      setError('Add at least 2 entities before running consolidation')
      return
    }
    setConsolidating(true)
    setError('')
    setResult(null)
    try {
      const data = await runConsolidation(dealId!)
      setResult(data)
      if (data.preliminary_stamp) setStamp(data.preliminary_stamp)
    } catch (err: unknown) {
      setError(formatApiError(err))
    } finally {
      setConsolidating(false)
    }
  }

  const canManage = ['cpa', 'super_admin'].includes(user?.role ?? '')

  if (loading) {
    return (
      <div className="p-8 text-sm text-gray-500">Loading entity structure…</div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Network className="w-5 h-5 text-blue-600" />
          <h1 className="text-xl font-semibold text-gray-900">Entity Structure</h1>
        </div>
        <div className="flex gap-2">
          {canManage && (
            <button
              onClick={() => setShowAddForm(v => !v)}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              Add Entity
            </button>
          )}
          <button
            onClick={handleConsolidate}
            disabled={consolidating || entities.length < 2}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
          >
            <Play className="w-4 h-4" />
            {consolidating ? 'Running…' : 'Run Consolidation'}
          </button>
        </div>
      </div>

      {/* CONSOL-FIX-1 P0 — page-level stamp, always on while under review. */}
      <PreliminaryStampBanner stamp={stamp} />

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Add Entity Form (QOE-RECON-1 — extracted, see entityStructure/AddEntityForm.tsx) */}
      {showAddForm && (
        <AddEntityForm
          form={form}
          setForm={setForm}
          formError={formError}
          saving={saving}
          entities={entities}
          onSave={handleAdd}
          onCancel={() => { setShowAddForm(false); setFormError('') }}
        />
      )}

      {/* Entity Table (QOE-RECON-1 — extracted, see entityStructure/EntityTable.tsx) */}
      <EntityTable
        entities={entities}
        canManage={canManage}
        deleting={deleting}
        onDelete={handleDelete}
      />

      {/* Consolidation Results (QOE-RECON-1 — extracted, see
          entityStructure/ConsolidationResults.tsx) */}
      {result && (
        <ConsolidationResults
          result={result}
          stamp={stamp}
          dealId={dealId!}
          entities={entities}
        />
      )}
    </div>
  )
}
